import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Shield, AlertTriangle, CheckCircle2, Copy,
    Activity, Info, Code2, Settings2
} from 'lucide-react'

type TabType = 'generator' | 'current' | 'docs'

interface CspDirective {
    name: string
    value: string
    risk: 'safe' | 'moderate' | 'strict'
    desc: string
}

const DIRECTIVES: CspDirective[] = [
    { name: 'default-src', value: "'self'", risk: 'strict', desc: 'Solo recursos del mismo origen' },
    { name: 'script-src', value: "'self' 'unsafe-inline'", risk: 'moderate', desc: 'Scripts propios + inline (necesario para Vite HMR)' },
    { name: 'style-src', value: "'self' 'unsafe-inline' fonts.googleapis.com", risk: 'moderate', desc: 'Estilos propios + inline + Google Fonts' },
    { name: 'font-src', value: "'self' fonts.gstatic.com", risk: 'safe', desc: 'Fuentes propias + Google Fonts CDN' },
    { name: 'img-src', value: "'self' data: blob:", risk: 'safe', desc: 'Imágenes propias + data URIs + blobs' },
    { name: 'connect-src', value: "'self' *.supabase.co wss://*.supabase.co", risk: 'safe', desc: 'APIs Supabase REST + Realtime WebSocket' },
    { name: 'frame-ancestors', value: "'none'", risk: 'strict', desc: 'Previene clickjacking — no permite iframes' },
    { name: 'base-uri', value: "'self'", risk: 'strict', desc: 'Previene base tag injection' },
    { name: 'form-action', value: "'self'", risk: 'strict', desc: 'Forms solo al mismo origen' },
    { name: 'upgrade-insecure-requests', value: '', risk: 'strict', desc: 'Fuerza HTTPS en todos los recursos' },
]

export default function CspPage() {
    const [tab, setTab] = useState<TabType>('generator')
    const [copied, setCopied] = useState(false)

    const fullPolicy = DIRECTIVES.map(d => `${d.name} ${d.value}`.trim()).join('; ')

    const copyPolicy = () => {
        navigator.clipboard.writeText(fullPolicy)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const riskStyles = {
        safe: { color: 'text-neon-green', bg: 'bg-neon-green/10' },
        moderate: { color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
        strict: { color: 'text-blue-400', bg: 'bg-blue-500/10' },
    }

    const tabs = [
        { id: 'generator' as const, label: 'Generator', icon: <Code2 size={16} /> },
        { id: 'current' as const, label: 'Directivas', icon: <Settings2 size={16} /> },
        { id: 'docs' as const, label: 'Guía', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Shield className="text-neon-blue" /> Content Security Policy
                </h1>
                <p className="text-gray-500 text-sm">Generador y monitor de CSP headers</p>
            </div>

            <FeatureNotEnabled moduleName="Content Security Policy" />

            <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                    { label: 'Directivas', value: String(DIRECTIVES.length), color: 'text-neon-blue' },
                    { label: 'Strict', value: String(DIRECTIVES.filter(d => d.risk === 'strict').length), color: 'text-blue-400' },
                    { label: 'Moderate', value: String(DIRECTIVES.filter(d => d.risk === 'moderate').length), color: 'text-yellow-400' },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className="text-2xl font-bold font-mono text-white">{s.value}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-1 ${s.color}`}>{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'generator' && (
                <Card>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-white font-bold text-sm flex items-center gap-2"><Code2 size={16} className="text-neon-blue" /> CSP Header Generado</h2>
                        <button onClick={copyPolicy} className="px-3 py-1.5 bg-neon-blue/10 text-neon-blue border border-neon-blue/20 rounded-lg text-xs font-medium hover:bg-neon-blue/20 transition-all flex items-center gap-1.5">
                            {copied ? <><CheckCircle2 size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
                        </button>
                    </div>
                    <pre className="bg-black/30 rounded-lg p-4 text-xs text-neon-green font-mono overflow-auto border border-white/5 break-all leading-relaxed">
                        Content-Security-Policy: {fullPolicy}
                    </pre>
                    <div className="mt-4 p-3 bg-black/20 rounded-lg">
                        <h3 className="text-gray-400 text-xs font-bold mb-2">Implementación en Vercel</h3>
                        <pre className="text-xs text-gray-300 font-mono">{`// vercel.json
{
  "headers": [{
    "source": "/(.*)",
    "headers": [{
      "key": "Content-Security-Policy",
      "value": "${fullPolicy}"
    }]
  }]
}`}</pre>
                    </div>
                </Card>
            )}

            {tab === 'current' && (
                <div className="space-y-2">
                    {DIRECTIVES.map(d => {
                        const r = riskStyles[d.risk]
                        return (
                            <Card key={d.name} className="!p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 size={14} className={r.color} />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <code className="text-neon-blue text-xs font-mono font-bold">{d.name}</code>
                                                <code className="text-white text-xs font-mono">{d.value}</code>
                                            </div>
                                            <p className="text-gray-500 text-xs mt-0.5">{d.desc}</p>
                                        </div>
                                    </div>
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${r.bg} ${r.color}`}>{d.risk}</span>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-3">¿Qué protege CSP?</h2>
                        <div className="space-y-2">
                            {[
                                { attack: 'XSS (Cross-Site Scripting)', protection: 'script-src restringe qué scripts pueden ejecutarse' },
                                { attack: 'Clickjacking', protection: 'frame-ancestors previene embedding en iframes maliciosos' },
                                { attack: 'Data Injection', protection: 'default-src limita fuentes de datos a orígenes confiables' },
                                { attack: 'Protocol Downgrade', protection: 'upgrade-insecure-requests fuerza HTTPS en todo' },
                            ].map(a => (
                                <div key={a.attack} className="p-3 bg-black/20 rounded-lg">
                                    <span className="text-red-400 text-sm font-medium">{a.attack}</span>
                                    <p className="text-gray-400 text-xs mt-0.5">→ {a.protection}</p>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card className="bg-yellow-500/5 border border-yellow-500/20">
                        <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-yellow-400 shrink-0" />
                            <div>
                                <h3 className="text-yellow-400 font-bold text-sm">⚠️ unsafe-inline</h3>
                                <p className="text-gray-400 text-xs mt-1">script-src y style-src incluyen 'unsafe-inline' porque Vite HMR y Tailwind lo requieren en desarrollo. En producción se recomienda usar hashes o nonces específicos.</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
